import React, { useState, useCallback } from 'react';
import { SketchPicker, ColorResult } from 'react-color';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

interface ColorPickerWithAlphaProps {
  label: string;
  value: string; // rgba format: "rgba(r, g, b, a)" or hex format: "#rrggbb"
  onChange: (value: string) => void;
  disableAlpha?: boolean; // If true, don't show alpha slider and return hex format
}

export default function ColorPickerWithAlpha({
  label,
  value,
  onChange,
  disableAlpha = false,
}: ColorPickerWithAlphaProps): JSX.Element {
  const [tempColor, setTempColor] = useState<ColorResult | null>(null);

  // Parse rgba or hex value to react-color format
  const parseColor = useCallback((color: string) => {
    // Try rgba format first
    const rgbaMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
    if (rgbaMatch) {
      return {
        r: parseInt(rgbaMatch[1]),
        g: parseInt(rgbaMatch[2]),
        b: parseInt(rgbaMatch[3]),
        a: rgbaMatch[4] ? parseFloat(rgbaMatch[4]) : 1,
      };
    }
    // Try hex format
    const hexMatch = color.match(/^#([0-9A-Fa-f]{6})$/);
    if (hexMatch) {
      const hex = hexMatch[1];
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
        a: 1,
      };
    }
    return { r: 0, g: 0, b: 0, a: 1 };
  }, []);

  const { r, g, b, a } = parseColor(value);
  const colorValue = disableAlpha 
    ? { r, g, b } 
    : { r, g, b, a: a ?? 1 };

  const handleColorChange = (color: ColorResult) => {
    setTempColor(color);
    const { r, g, b, a } = color.rgb;
    if (disableAlpha) {
      const hex = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
      onChange(hex);
    } else {
      onChange(`rgba(${r}, ${g}, ${b}, ${a ?? 1})`);
    }
  };

  const displayColor = tempColor ? tempColor.rgb : { r, g, b, a };
  const displayString = disableAlpha 
    ? `#${displayColor.r.toString(16).padStart(2, '0')}${displayColor.g.toString(16).padStart(2, '0')}${displayColor.b.toString(16).padStart(2, '0')}`
    : `rgba(${displayColor.r}, ${displayColor.g}, ${displayColor.b}, ${displayColor.a ?? 1})`;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="w-12 h-10 p-1 border-border bg-transparent hover:bg-card focus:ring-primary/50"
              style={{
                backgroundColor: disableAlpha 
                  ? `rgb(${displayColor.r}, ${displayColor.g}, ${displayColor.b})`
                  : `rgba(${displayColor.r}, ${displayColor.g}, ${displayColor.b}, ${displayColor.a ?? 1})`,
              }}
            />
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 border-border bg-muted shadow-2xl">
            <div className="sketch-picker-container p-2">
              <style>{`
                .sketch-picker {
                  background: #1e293b !important;
                  border: 1px solid #334155 !important;
                  box-shadow: none !important;
                  border-radius: 8px !important;
                }
                .sketch-picker > div {
                  background: #1e293b !important;
                }
                .sketch-picker input {
                  background: #0f172a !important;
                  color: #e2e8f0 !important;
                  border: 1px solid #334155 !important;
                  border-radius: 4px !important;
                  font-family: inherit !important;
                }
                .sketch-picker label {
                  color: #94a3b8 !important;
                  font-family: inherit !important;
                  font-weight: 500 !important;
                }
              `}</style>
              <SketchPicker
                color={tempColor ? tempColor.rgb : colorValue}
                onChange={handleColorChange}
                disableAlpha={disableAlpha}
                width="220px"
                presetColors={[]}
              />
            </div>
          </PopoverContent>
        </Popover>
        <div className="flex-1 space-y-1.5">
          <Label className="text-[10px] text-muted-foreground uppercase font-bold tracking-tight">
            {label} ({disableAlpha ? 'hex' : 'rgba'})
          </Label>
          <Input
            value={displayString}
            onChange={(e) => onChange(e.target.value)}
            className="h-9 bg-muted border-border text-foreground text-xs focus-visible:ring-primary/50"
            placeholder={disableAlpha ? "#000000" : "rgba(0, 0, 0, 0.8)"}
          />
        </div>
      </div>
    </div>
  );
}
